/**
 * SLDHeader — toolbar for the SLD viewer with diagram selection, zoom controls, and toggle buttons.
 * Extracted from SLDViewerModal.tsx (no behavior changes).
 */

import type { SLDDiagram } from '@/stores/monitoringStore';

export interface SLDHeaderProps {
  sldDiagrams: SLDDiagram[];
  activeDiagramId: string | null;
  activeDiagram: SLDDiagram | null;
  zoomPct: number;
  isPlacing: boolean;
  showSidePanel: boolean;
  multiViewActive: boolean;
  hasImage: boolean;
  onDiagramChange: (diagramId: string) => void;
  onUploadClick: () => void;
  onDeleteDiagram: () => void;
  onCancelPlacement: () => void;
  onToggleSidePanel: () => void;
  onToggleMultiView: () => void;
  onSave: () => void;
  onFitToView: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onClose: (e: React.MouseEvent) => void;
}

export default function SLDHeader({
  sldDiagrams,
  activeDiagramId,
  activeDiagram,
  zoomPct,
  isPlacing,
  showSidePanel,
  multiViewActive,
  hasImage,
  onDiagramChange,
  onUploadClick,
  onDeleteDiagram,
  onCancelPlacement,
  onToggleSidePanel,
  onToggleMultiView,
  onSave,
  onFitToView,
  onZoomIn,
  onZoomOut,
  onClose,
}: SLDHeaderProps) {
  return (
    <div className="sld-header">
      <div className="sld-header-left">
        <span className="sld-title">Single Line Diagram</span>
        <select
          value={activeDiagramId ?? ''}
          onChange={(e) => e.target.value && onDiagramChange(e.target.value)}
        >
          {sldDiagrams.length === 0 && <option value="">No diagrams</option>}
          {sldDiagrams.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <button onClick={onUploadClick} title="Upload PDF or Image">
          Upload
        </button>
        <button
          className="sld-delete-btn"
          onClick={onDeleteDiagram}
          disabled={!activeDiagram}
          title="Delete current diagram"
        >
          Delete
        </button>
      </div>

      <div className="sld-header-center">
        {isPlacing ? (
          <button className="sld-add-point-btn active" onClick={onCancelPlacement}>
            Cancel Placement
          </button>
        ) : (
          <button
            className="sld-add-point-btn"
            disabled={!activeDiagram || multiViewActive}
            onClick={onToggleSidePanel}
          >
            {showSidePanel ? 'Hide Panel' : 'Show Panel'}
          </button>
        )}
        <button
          className={`sld-multi-view-btn${multiViewActive ? ' active' : ''}`}
          disabled={sldDiagrams.length === 0}
          onClick={onToggleMultiView}
        >
          {multiViewActive ? 'Single View' : 'Multi View'}
        </button>
        <button className="sld-save-btn" onClick={onSave}>
          Save
        </button>
      </div>

      <div className="sld-header-right">
        <button onClick={onFitToView} disabled={!hasImage}>
          Fit
        </button>
        <button onClick={onZoomIn} disabled={!hasImage}>
          +
        </button>
        <span className="sld-zoom-display">{zoomPct}%</span>
        <button onClick={onZoomOut} disabled={!hasImage}>
          {'\u2212'}
        </button>
        <button className="sld-close-btn" onClick={onClose}>
          {'\u2715'}
        </button>
      </div>
    </div>
  );
}
